import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronDown,
  faChevronRight,
  faArchive,
  faCopy,
  faCodeBranch,
  faClockRotateLeft,
  faImage,
  faVideo,
  faFile,
  faExclamationCircle,
  faCheckCircle,
  faShieldHalved,
  faCamera,
  faFileAlt,
  faPencil,
} from '@fortawesome/free-solid-svg-icons';
import DOMPurify from 'dompurify';
import { useState } from 'react';
import { useHasPermission } from '@/hooks/use-permissions';
import { SkeletonPage } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { toast, swalConfirm } from '@/lib/swal';
import { cn } from '@/lib/cn';
import { useProcedureTemplate } from './hooks/useProcedureTemplates';
import { useArchiveProcedure, useNewVersionProcedure } from './hooks/useProcedureMutations';
import { ProcedureStatusBadge } from './components/ProcedureStatusBadge';
import { ProcedureTypeBadge } from './components/ProcedureTypeBadge';
import { CloneDialog } from './components/CloneDialog';
import { ROUTES } from '@/routes/routes';
import type { ProcedureSection, ProcedureStep } from '@/services/procedure-template.service';
import axios from 'axios';

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

// ── Step row (read-only) ──────────────────────────────────────────────────────

function StepReadRow({ step, stepIndex }: { step: ProcedureStep; stepIndex: number }) {
  const [expanded, setExpanded] = useState(false);
  const hasContent = !!(
    step.description ||
    step.instructionContent ||
    (step.referenceMaterials?.length ?? 0) > 0 ||
    step.isMandatory ||
    step.passFailRequired ||
    step.authorizationRequired ||
    step.evidenceRequired
  );

  return (
    <div className="rounded-lg border border-border bg-background">
      <button
        type="button"
        onClick={() => {
          if (hasContent) setExpanded((v) => !v);
        }}
        className={cn(
          'flex w-full items-center gap-3 px-4 py-3 text-left',
          hasContent ? 'cursor-pointer hover:bg-muted/30 transition-colors' : 'cursor-default',
        )}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border text-xs font-semibold text-muted-foreground">
          {stepIndex + 1}
        </span>
        <span className="flex-1 text-sm font-medium text-foreground">{step.title}</span>
        <div className="flex shrink-0 flex-wrap items-center gap-1">
          {step.isMandatory && (
            <span className="hidden items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 sm:flex">
              <FontAwesomeIcon icon={faExclamationCircle} className="h-2.5 w-2.5" />
              Mandatory
            </span>
          )}
          {step.passFailRequired && (
            <span className="hidden items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-medium text-green-700 sm:flex">
              <FontAwesomeIcon icon={faCheckCircle} className="h-2.5 w-2.5" />
              Pass/Fail
            </span>
          )}
          {step.authorizationRequired && (
            <span className="hidden items-center gap-1 rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium text-sky-700 sm:flex">
              <FontAwesomeIcon icon={faShieldHalved} className="h-2.5 w-2.5" />
              Auth
            </span>
          )}
          {step.evidenceRequired && (
            <span className="hidden items-center gap-1 rounded-full bg-orange-500/10 px-2 py-0.5 text-[10px] font-medium text-orange-700 sm:flex">
              <FontAwesomeIcon icon={faCamera} className="h-2.5 w-2.5" />
              Evidence
            </span>
          )}
          {hasContent && (
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              className="ml-1 h-3 w-3 text-muted-foreground"
            />
          )}
        </div>
      </button>

      {expanded && hasContent && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
          {step.description && <p className="text-sm text-muted-foreground">{step.description}</p>}
          {step.instructionContent && (
            <div
              className="prose prose-sm max-w-none text-foreground dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(step.instructionContent) }}
            />
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-4">
            {(
              [
                { label: 'Mandatory', value: step.isMandatory },
                { label: 'Pass/Fail Required', value: step.passFailRequired },
                { label: 'Authorization Required', value: step.authorizationRequired },
                { label: 'Comments Allowed', value: step.commentsAllowed },
              ] as const
            ).map(({ label, value }) => (
              <div key={label}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {label}
                </p>
                <p
                  className={cn(
                    'text-xs font-medium',
                    value ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {value ? 'Yes' : 'No'}
                </p>
              </div>
            ))}
          </div>
          {step.evidenceRequired && (
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              {step.minPhotos > 0 && (
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faCamera} className="h-3 w-3" />
                  Min {step.minPhotos} photo{step.minPhotos > 1 ? 's' : ''}
                </span>
              )}
              {step.minVideos > 0 && (
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faVideo} className="h-3 w-3" />
                  Min {step.minVideos} video{step.minVideos > 1 ? 's' : ''}
                </span>
              )}
              {step.minDocuments > 0 && (
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faFileAlt} className="h-3 w-3" />
                  Min {step.minDocuments} document{step.minDocuments > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
          {step.referenceMaterials.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Reference Materials
              </p>
              <div className="flex flex-wrap gap-2">
                {step.referenceMaterials.map((m) => (
                  <a
                    key={m.id}
                    href={m.signedUrl}
                    download={m.fileName}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/20 px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors"
                  >
                    <FontAwesomeIcon
                      icon={
                        m.materialType === 'IMAGE'
                          ? faImage
                          : m.materialType === 'VIDEO'
                            ? faVideo
                            : faFile
                      }
                      className="h-3 w-3 text-muted-foreground"
                    />
                    <span className="max-w-[200px] truncate">{m.fileName}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section card (read-only) ──────────────────────────────────────────────────

function SectionReadCard({
  section,
  sectionIndex,
}: {
  section: ProcedureSection;
  sectionIndex: number;
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-muted/30 transition-colors"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
          {sectionIndex + 1}
        </span>
        <span className="flex-1 text-base font-semibold text-foreground">{section.title}</span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {section.steps.length} step{section.steps.length !== 1 ? 's' : ''}
        </span>
        <FontAwesomeIcon
          icon={expanded ? faChevronDown : faChevronRight}
          className="h-4 w-4 text-muted-foreground"
        />
      </button>
      {expanded && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-2">
          {section.steps.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No steps in this section.
            </p>
          )}
          {section.steps.map((step, idx) => (
            <StepReadRow key={step.id} step={step} stepIndex={idx} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main view page ────────────────────────────────────────────────────────────

export function ProcedureViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const can = useHasPermission();

  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const { data: template, isLoading, error: loadError } = useProcedureTemplate(id);
  const archiveMutation = useArchiveProcedure(id ?? '');
  const newVersionMutation = useNewVersionProcedure();

  if (isLoading) return <SkeletonPage />;
  if (loadError || !template) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-sm text-destructive">Failed to load procedure</p>
        <button
          onClick={() => navigate(ROUTES.PROCEDURES)}
          className="mt-3 text-sm text-primary hover:underline"
        >
          Back to Library
        </button>
      </div>
    );
  }

  // Redirect draft to edit
  if (template.status === 'DRAFT') {
    navigate(ROUTES.PROCEDURE_EDIT.replace(':id', template.id), { replace: true });
    return null;
  }

  const totalSteps = template.sections.reduce((acc, s) => acc + s.steps.length, 0);
  const mandatorySteps = template.sections
    .flatMap((s) => s.steps)
    .filter((st) => st.isMandatory).length;

  async function handleArchive() {
    const ok = await swalConfirm({
      title: `Archive '${template!.title}'?`,
      text: 'This removes the procedure from new inspection assignments. Existing active inspections are unaffected.',
    });
    if (!ok) return;
    try {
      await archiveMutation.mutateAsync();
      toast.success('Archived');
      navigate(ROUTES.PROCEDURES);
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to archive'));
    }
  }

  async function handleNewVersion() {
    const ok = await swalConfirm({
      title: 'Create New Version?',
      text: `A new draft v${template!.versionNumber + 1} will be created from the published v${template!.versionNumber}. You will be redirected to the editor.`,
    });
    if (!ok) return;
    try {
      const result = await newVersionMutation.mutateAsync(template!.id);
      toast.success(`Draft v${result.versionNumber} created`);
      navigate(ROUTES.PROCEDURE_EDIT.replace(':id', result.id));
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to create version'));
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <button
          type="button"
          onClick={() => navigate(ROUTES.PROCEDURES)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">{template.title}</h1>
            <span className="text-sm text-muted-foreground">v{template.versionNumber}</span>
            <ProcedureStatusBadge status={template.status} />
            <ProcedureTypeBadge procedureType={template.procedureType} />
          </div>
          {template.description && (
            <p className="mt-1 text-sm text-muted-foreground">{template.description}</p>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {template.status === 'PUBLISHED' && can('procedure:update') && (
            <Button
              variant="ghost"
              onClick={() =>
                navigate(`${ROUTES.PROCEDURE_EDIT.replace(':id', template.id)}?mode=quick-edit`)
              }
              className="h-9 border border-border"
            >
              <FontAwesomeIcon icon={faPencil} className="mr-2 h-3.5 w-3.5" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
          )}
          {template.status === 'PUBLISHED' && can('procedure:clone') && (
            <Button
              variant="ghost"
              onClick={() => void handleNewVersion()}
              disabled={newVersionMutation.isPending}
              className="h-9 border border-border"
            >
              <FontAwesomeIcon icon={faCodeBranch} className="mr-2 h-3.5 w-3.5" />
              <span className="hidden sm:inline">New Version</span>
            </Button>
          )}
          {can('procedure:clone') && (
            <Button
              variant="ghost"
              onClick={() => setCloneDialogOpen(true)}
              className="h-9 border border-border"
            >
              <FontAwesomeIcon icon={faCopy} className="mr-2 h-3.5 w-3.5" />
              <span className="hidden sm:inline">Clone</span>
            </Button>
          )}
          {template.status === 'PUBLISHED' && can('procedure:archive') && (
            <Button
              variant="ghost"
              onClick={() => void handleArchive()}
              disabled={archiveMutation.isPending}
              className="h-9 border border-amber-300/50 text-amber-700 hover:bg-amber-500/10"
            >
              <FontAwesomeIcon icon={faArchive} className="mr-2 h-3.5 w-3.5" />
              <span className="hidden sm:inline">Archive</span>
            </Button>
          )}
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Sections', value: template.sections.length },
          { label: 'Total Steps', value: totalSteps },
          { label: 'Mandatory', value: mandatorySteps },
          { label: 'Customer Visible', value: template.customerVisible ? 'Yes' : 'No' },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-xl border border-border bg-card p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{value}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      {/* Meta panel */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {template.equipmentModel && (
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Equipment Model
            </p>
            <p className="text-sm font-medium text-foreground">{template.equipmentModel.name}</p>
          </div>
        )}
        {template.publishedAt && (
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Published
            </p>
            <p className="text-sm font-medium text-foreground">
              {new Date(template.publishedAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </p>
          </div>
        )}
        {template.archivedAt && (
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Archived
            </p>
            <p className="text-sm font-medium text-foreground">
              {new Date(template.archivedAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </p>
          </div>
        )}
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Created By
          </p>
          <p className="truncate text-sm font-medium text-foreground">
            {template.createdBy.fullName}
          </p>
        </div>
      </div>

      {/* Version navigation */}
      <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
        <div className="text-sm text-muted-foreground">
          {template.previousVersionId && (
            <Link
              to={ROUTES.PROCEDURE_VIEW.replace(':id', template.previousVersionId)}
              className="text-primary hover:underline"
            >
              ← Previous version
            </Link>
          )}
        </div>
        <Link
          to={ROUTES.PROCEDURE_VERSIONS.replace(':id', template.id)}
          className="flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <FontAwesomeIcon icon={faClockRotateLeft} className="h-3.5 w-3.5" />
          Version history
        </Link>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-foreground">Procedure Steps</h2>
          <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
            {template.sections.length}
          </span>
        </div>
        {template.sections.length === 0 && (
          <div className="rounded-xl border border-border py-16 text-center">
            <p className="text-sm text-muted-foreground">No sections in this procedure.</p>
          </div>
        )}
        {template.sections.map((section, idx) => (
          <SectionReadCard key={section.id} section={section} sectionIndex={idx} />
        ))}
      </div>

      {cloneDialogOpen && (
        <CloneDialog
          source={{ id: template.id, title: template.title, procedureType: template.procedureType }}
          onClose={() => setCloneDialogOpen(false)}
        />
      )}
    </div>
  );
}
